/**
 * In-progress batch scan persistence. Confirmed lines are written to Supabase (or the offline
 * queue) as you go; this store also keeps the session list + any saves still in flight so a
 * dropped connection, reload, or tab kill does not lose visible scans before the client hears back.
 *
 * Label **photos are never written** to this store — only parsed text (name, strength, etc.),
 * step, and confirmed-line summaries (`sessionLog` / `outboundPending` use display names only).
 *
 * Primary store: IndexedDB (much larger quota than localStorage — multi‑MB drafts).
 * Fallback: localStorage when IDB is unavailable (private mode, old browsers).
 */

export const BATCH_SCAN_RECOVERY_VERSION = 1 as const;
export const BATCH_SCAN_RECOVERY_MAX_AGE_MS = 72 * 60 * 60 * 1000; // 72h

const IDB_NAME = "ed_expirydesk";
const IDB_VERSION = 1;
const IDB_STORE = "batch_scan_recovery";

export type BatchScanRecoveryDraft = {
  labelBase64: string | null;
  labelMediaType: string;
  month: number;
  year: number;
  apiStatus: string;
  name: string;
  /** Legacy v1 drafts used `dosage` for strength */
  dosage?: string;
  strength?: string;
  dosageForm: string;
  unitType: string;
  quantity: number;
  /** Step 3 keypad: null = default display 1, "" = cleared */
  quantityBuffer?: string | null;
  attentionName?: boolean;
  attentionStrength?: boolean;
  attentionForm?: boolean;
  attentionExpiry?: boolean;
  unit: string;
  matchStatus: string;
  suggestedName: string | null;
  /** Raw label AI product name before user edits (optional; v2 recovery). */
  aiParsedName?: string;
};

/** One confirmed line in the batch UI (matches `BatchScanView` session rows). */
export type BatchScanSessionLogEntry = {
  key: number;
  line: string;
  quantity: number;
};

/** Payload passed to `onSaveItem` / `handleBatchScanSaveItem` (serializable subset). */
export type BatchScanOutboundItem = {
  id: number;
  name: string;
  dosage: string;
  quantity: number;
  unit: string;
  expiryMonth: number;
  expiryYear: number;
  pricePerBox: number | null;
  status: string | null;
  matchStatus: string;
  suggestedName: string | null;
  originalName: string | null;
};

export type BatchScanRecoveryV1 = {
  v: typeof BATCH_SCAN_RECOVERY_VERSION;
  userId: string;
  savedAt: number;
  step: "capture" | "date" | "confirm";
  itemIndex: number;
  confirmedCount: number;
  draft: BatchScanRecoveryDraft;
  /** Restored session list (optional for older saved blobs). */
  sessionLog?: BatchScanSessionLogEntry[];
  /** Saves not yet acknowledged by the host (replay on resume). */
  outboundPending?: { key: number; item: BatchScanOutboundItem }[];
};

function storageKey(userId: string) {
  return `ed_batch_scan_recovery_v${BATCH_SCAN_RECOVERY_VERSION}_${userId}`;
}

function normalizeDraft(draft: BatchScanRecoveryDraft): void {
  const dr = draft as BatchScanRecoveryDraft & { dosage?: string };
  if (typeof dr.strength !== "string") {
    dr.strength = typeof dr.dosage === "string" ? dr.dosage : "";
  }
  if (typeof dr.dosageForm !== "string") dr.dosageForm = "";
  if (typeof dr.unitType !== "string") dr.unitType = "";
  if (dr.quantityBuffer !== null && dr.quantityBuffer !== undefined && typeof dr.quantityBuffer !== "string") {
    dr.quantityBuffer = "";
  }
  if (typeof dr.attentionName !== "boolean") dr.attentionName = false;
  if (typeof dr.attentionStrength !== "boolean") dr.attentionStrength = false;
  if (typeof dr.attentionForm !== "boolean") dr.attentionForm = false;
  /** Legacy: Claude outcome was `ok` / `error`; flow now uses `ready` for any resolved parse. */
  if (dr.apiStatus === "ok" || dr.apiStatus === "error") dr.apiStatus = "ready";
  const validParse = new Set(["idle", "loading", "ready"]);
  if (typeof dr.apiStatus !== "string" || !validParse.has(dr.apiStatus)) dr.apiStatus = "idle";
}

function parseSessionLog(raw: unknown): BatchScanSessionLogEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: BatchScanSessionLogEntry[] = [];
  for (const x of raw) {
    if (!x || typeof x !== "object") continue;
    const o = x as BatchScanSessionLogEntry;
    if (typeof o.key !== "number" || !Number.isFinite(o.key)) continue;
    out.push({
      key: o.key,
      line: typeof o.line === "string" ? o.line : "",
      quantity: typeof o.quantity === "number" && Number.isFinite(o.quantity) ? o.quantity : 0,
    });
  }
  return out.length ? out : undefined;
}

function parseOutboundPending(raw: unknown): { key: number; item: BatchScanOutboundItem }[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: { key: number; item: BatchScanOutboundItem }[] = [];
  for (const x of raw) {
    if (!x || typeof x !== "object") continue;
    const row = x as { key?: unknown; item?: unknown };
    if (typeof row.key !== "number" || !Number.isFinite(row.key)) continue;
    const it = row.item;
    if (!it || typeof it !== "object") continue;
    const i = it as Record<string, unknown>;
    if (typeof i.expiryMonth !== "number" || typeof i.expiryYear !== "number") continue;
    const id = typeof i.id === "number" && Number.isFinite(i.id) ? i.id : row.key;
    out.push({
      key: row.key,
      item: {
        id,
        name: typeof i.name === "string" ? i.name : "",
        dosage: typeof i.dosage === "string" ? i.dosage : "",
        quantity: typeof i.quantity === "number" && Number.isFinite(i.quantity) ? i.quantity : 0,
        unit: typeof i.unit === "string" ? i.unit : "unit",
        expiryMonth: i.expiryMonth,
        expiryYear: i.expiryYear,
        pricePerBox: i.pricePerBox === null || typeof i.pricePerBox === "number" ? (i.pricePerBox as number | null) : null,
        status: i.status === null || typeof i.status === "string" ? (i.status as string | null) : null,
        matchStatus: typeof i.matchStatus === "string" ? i.matchStatus : "not_checked",
        suggestedName: i.suggestedName === null || typeof i.suggestedName === "string" ? (i.suggestedName as string | null) : null,
        originalName: i.originalName === null || typeof i.originalName === "string" ? (i.originalName as string | null) : null,
      },
    });
  }
  return out.length ? out : undefined;
}

function validatePayload(data: unknown, userId: string): BatchScanRecoveryV1 | null {
  if (!data || typeof data !== "object") return null;
  const d = data as BatchScanRecoveryV1;
  if (d.v !== BATCH_SCAN_RECOVERY_VERSION || d.userId !== userId) return null;
  if (typeof d.savedAt !== "number" || Date.now() - d.savedAt > BATCH_SCAN_RECOVERY_MAX_AGE_MS) return null;
  if (!d.draft || typeof d.step !== "string") return null;
  normalizeDraft(d.draft);
  const sessionLog = parseSessionLog(d.sessionLog);
  const outboundPending = parseOutboundPending(d.outboundPending);
  const { sessionLog: _ignoreSl, outboundPending: _ignoreOp, ...rest } = d;
  const base: BatchScanRecoveryV1 = { ...rest, sessionLog, outboundPending };
  if (sessionLog?.length && typeof base.confirmedCount === "number" && base.confirmedCount < sessionLog.length) {
    base.confirmedCount = sessionLog.length;
  }
  /** Never hydrate in-memory drafts from stored photos (recovery is name/text only). */
  base.draft.labelBase64 = null;
  return base;
}

function legacySave(payload: BatchScanRecoveryV1): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(storageKey(payload.userId), JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

function legacyLoad(userId: string): BatchScanRecoveryV1 | null {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = validatePayload(JSON.parse(raw), userId);
    if (!parsed) legacyClear(userId);
    return parsed;
  } catch {
    legacyClear(userId);
    return null;
  }
}

function legacyClear(userId: string) {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    /* ignore */
  }
}

let idbSingleton: IDBDatabase | null = null;
let idbOpenPromise: Promise<IDBDatabase | null> | null = null;

function attachDbLifecycle(db: IDBDatabase) {
  db.onversionchange = () => {
    try {
      db.close();
    } catch {
      /* ignore */
    }
    idbSingleton = null;
    idbOpenPromise = null;
  };
}

/** One connection per tab — avoids open/close churn during frequent debounced saves. */
function getIndexedDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (idbSingleton) return Promise.resolve(idbSingleton);
  if (idbOpenPromise) return idbOpenPromise;

  idbOpenPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, IDB_VERSION);
      req.onerror = () => {
        idbOpenPromise = null;
        resolve(null);
      };
      req.onsuccess = () => {
        const db = req.result;
        attachDbLifecycle(db);
        idbSingleton = db;
        idbOpenPromise = null;
        resolve(db);
      };
      req.onupgradeneeded = () => {
        const database = req.result;
        if (!database.objectStoreNames.contains(IDB_STORE)) {
          database.createObjectStore(IDB_STORE, { keyPath: "userId" });
        }
      };
    } catch {
      idbOpenPromise = null;
      resolve(null);
    }
  });
  return idbOpenPromise;
}

async function idbPut(payload: BatchScanRecoveryV1): Promise<boolean> {
  const db = await getIndexedDb();
  if (!db) return false;
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(IDB_STORE).put(payload);
    });
    return true;
  } catch {
    return false;
  }
}

async function idbGet(userId: string): Promise<BatchScanRecoveryV1 | null> {
  const db = await getIndexedDb();
  if (!db) return null;
  try {
    const row = await new Promise<BatchScanRecoveryV1 | undefined>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      tx.onerror = () => reject(tx.error);
      const req = tx.objectStore(IDB_STORE).get(userId);
      req.onsuccess = () => resolve(req.result as BatchScanRecoveryV1 | undefined);
    });
    if (!row) return null;
    const parsed = validatePayload(row, userId);
    if (!parsed) {
      await idbDelete(userId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

async function idbDelete(userId: string): Promise<void> {
  const db = await getIndexedDb();
  if (!db) return;
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(IDB_STORE).delete(userId);
    });
  } catch {
    /* ignore */
  }
}

/** Prefer IndexedDB; fall back to localStorage. */
export async function saveBatchScanRecovery(payload: BatchScanRecoveryV1): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const toStore: BatchScanRecoveryV1 = {
    ...payload,
    draft: { ...payload.draft, labelBase64: null },
  };
  const ok = await idbPut(toStore);
  if (ok) {
    legacyClear(payload.userId);
    return true;
  }
  return legacySave(toStore);
}

export async function loadBatchScanRecovery(userId: string): Promise<BatchScanRecoveryV1 | null> {
  if (typeof window === "undefined" || !userId) return null;
  const fromIdb = await idbGet(userId);
  if (fromIdb) {
    legacyClear(userId);
    return fromIdb;
  }
  const legacy = legacyLoad(userId);
  if (legacy) {
    const migrated = await idbPut(legacy);
    if (migrated) legacyClear(userId);
  }
  return legacy;
}

export async function clearBatchScanRecovery(userId: string | null | undefined): Promise<void> {
  if (typeof window === "undefined" || !userId) return;
  legacyClear(userId);
  await idbDelete(userId);
}

/** True if leaving the batch UI could feel like data loss (draft or any confirmed lines this session). */
export function batchScanExitIsRisky(params: {
  confirmedCount: number;
  hasActiveUnconfirmed: boolean;
}): boolean {
  return params.confirmedCount > 0 || params.hasActiveUnconfirmed;
}

/** True when saved recovery includes an in-progress draft (text-only after load; step / name may still indicate work). */
export function batchScanRecoveryHasResumableDraft(r: Pick<BatchScanRecoveryV1, "step" | "draft">): boolean {
  if (r.step === "date" || r.step === "confirm") return true;
  if (String(r.draft?.name || "").trim()) return true;
  return Boolean(r.draft?.labelBase64);
}
